import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { driversService } from '../../services/driversService';
import { restaurantsService } from '../../services/restaurantsService';
import { DriverLocationResponse } from '../../types/drivers';
import { RestaurantBranchResponse, ApplicationStatus } from '../../types/restaurants';
import SearchDriversMap from '../map/SearchDriversMap';
import LocationPicker from '../map/LocationPicker';
import './Page.css';

const RESTAURANT_CACHE_KEY = 'termez_restaurant_branches';

interface BranchWithRestaurant extends RestaurantBranchResponse {
  restaurantName: string;
}

const SearchDrivers: React.FC = () => {
  const { t } = useTranslation();
  const [centerLatitude, setCenterLatitude] = useState('37.2242');
  const [centerLongitude, setCenterLongitude] = useState('67.2783');
  const [radius, setRadius] = useState('5000');
  const [limit, setLimit] = useState('50');
  const [drivers, setDrivers] = useState<DriverLocationResponse[]>([]);
  const [restaurantBranches, setRestaurantBranches] = useState<BranchWithRestaurant[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const handleLocationSelect = (lat: number, lng: number) => {
    setCenterLatitude(lat.toFixed(6));
    setCenterLongitude(lng.toFixed(6));
  };

  // Load cached restaurant branches on mount
  useEffect(() => {
    const cachedData = localStorage.getItem(RESTAURANT_CACHE_KEY);
    if (cachedData) {
      try {
        const branches: BranchWithRestaurant[] = JSON.parse(cachedData);
        console.log('Loaded cached restaurant branches:', branches.length);
        setRestaurantBranches(branches);
      } catch (e) {
        console.error('Failed to load cached branches:', e);
      }
    }
  }, []);

  // Fetch restaurant branches
  const fetchRestaurantBranches = async () => {
    setLoadingRestaurants(true);
    try {
      console.log('Fetching restaurants from API...');
      const restaurantsResponse = await restaurantsService.getRestaurants({
        searchQuery: '',
        page: 0,
        pageSize: 100,
        applicationStatus: null
      });

      console.log('Restaurants response:', restaurantsResponse);

      if (restaurantsResponse.success && restaurantsResponse.data) {
        const allBranches: BranchWithRestaurant[] = [];

        console.log('Fetching details for', restaurantsResponse.data.length, 'restaurants');

        // Fetch details for each restaurant to get branches
        for (const restaurant of restaurantsResponse.data) {
          const detailsResponse = await restaurantsService.getRestaurantDetails(restaurant.id);
          if (detailsResponse.success && detailsResponse.data) {
            console.log(`Restaurant ${restaurant.name} has ${detailsResponse.data.branches.length} branches`);
            // Add restaurant name to each branch
            const branchesWithRestaurant = detailsResponse.data.branches.map(branch => ({
              ...branch,
              restaurantName: restaurant.name
            }));
            allBranches.push(...branchesWithRestaurant);
          }
        }

        console.log('Total branches found:', allBranches.length);

        // Update state
        setRestaurantBranches(allBranches);

        // Cache the data
        localStorage.setItem(RESTAURANT_CACHE_KEY, JSON.stringify(allBranches));
      } else {
        console.error('Failed to fetch restaurants:', restaurantsResponse.error);
      }
    } catch (error) {
      console.error('Failed to fetch restaurant branches:', error);
    } finally {
      setLoadingRestaurants(false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    const lat = parseFloat(centerLatitude);
    const lng = parseFloat(centerLongitude);
    const rad = parseInt(radius);
    const lim = parseInt(limit);

    // Validation
    if (isNaN(lat) || isNaN(lng)) {
      setError('Please enter valid coordinates');
      return;
    }

    if (isNaN(rad) || rad <= 0) {
      setError('Please enter a valid radius');
      return;
    }

    if (isNaN(lim) || lim <= 0 || lim > 100) {
      setError('Please enter a valid limit (1-100)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await driversService.searchDriversOnMap({
        centerLatitude: lat,
        centerLongitude: lng,
        radius: rad,
        limit: lim
      });

      if (response.success && response.data) {
        setDrivers(response.data);
        setHasSearched(true);
      } else {
        setError(response.error || 'Failed to search drivers');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>{t('searchDriversPage.title')}</h1>
        <p>{t('searchDriversPage.subtitle')}</p>
      </div>

      <div className="page-content">
        {/* Location Picker */}
        <div className="detail-card full-width">
          <h3>{t('searchDriversPage.selectCenter')}</h3>
          <LocationPicker
            latitude={parseFloat(centerLatitude)}
            longitude={parseFloat(centerLongitude)}
            onLocationSelect={handleLocationSelect}
            height="450px"
          />
        </div>

        {/* Search Form */}
        <div className="detail-card full-width" style={{ marginTop: '1.5rem' }}>
          <h3>{t('searchDriversPage.searchParameters')}</h3>
          <form onSubmit={handleSearch}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('searchDriversPage.centerLatitude')}
                </label>
                <input
                  type="text"
                  value={centerLatitude}
                  onChange={(e) => setCenterLatitude(e.target.value)}
                  placeholder="e.g., 41.2995"
                  className="search-input"
                  required
                  disabled={loading}
                  style={{ backgroundColor: '#f8f9fa' }}
                />
                <small style={{ color: '#666', fontSize: '0.85rem' }}>📍 {t('searchDriversPage.autoFilled')}</small>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('searchDriversPage.centerLongitude')}
                </label>
                <input
                  type="text"
                  value={centerLongitude}
                  onChange={(e) => setCenterLongitude(e.target.value)}
                  placeholder="e.g., 69.2401"
                  className="search-input"
                  required
                  disabled={loading}
                  style={{ backgroundColor: '#f8f9fa' }}
                />
                <small style={{ color: '#666', fontSize: '0.85rem' }}>📍 {t('searchDriversPage.autoFilled')}</small>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('searchDriversPage.radius')}
                </label>
                <input
                  type="number"
                  value={radius}
                  onChange={(e) => setRadius(e.target.value)}
                  placeholder={t('searchDriversPage.radiusPlaceholder')}
                  className="search-input"
                  required
                  disabled={loading}
                  min="1"
                />
                <small style={{ color: '#666', fontSize: '0.85rem' }}>{t('searchDriversPage.radiusHint')}</small>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('searchDriversPage.limit')}
                </label>
                <input
                  type="number"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  placeholder={t('searchDriversPage.limitPlaceholder')}
                  className="search-input"
                  required
                  disabled={loading}
                  min="1"
                  max="100"
                />
                <small style={{ color: '#666', fontSize: '0.85rem' }}>{t('searchDriversPage.limitHint')}</small>
              </div>
            </div>

            <button
              type="submit"
              className="search-button"
              disabled={loading}
              style={{ width: '100%', padding: '0.75rem' }}
            >
              {loading ? t('searchDriversPage.searching') : t('searchDriversPage.searchButton')}
            </button>
          </form>

          {/* Quick Location Buttons - Restaurant Branches */}
          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #dee2e6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                {t('searchDriversPage.quickLocations')} - {t('searchDriversPage.restaurants')} {restaurantBranches.length > 0 && `(${restaurantBranches.length})`}
              </div>
              <button
                className="filter-tag"
                onClick={fetchRestaurantBranches}
                disabled={loadingRestaurants}
                style={{
                  backgroundColor: loadingRestaurants ? '#6c757d' : '#007bff',
                  color: 'white',
                  border: 'none',
                  cursor: loadingRestaurants ? 'not-allowed' : 'pointer'
                }}
              >
                {loadingRestaurants ? `🔄 ${t('searchDriversPage.updating')}` : `🔄 ${t('searchDriversPage.updateLocations')}`}
              </button>
            </div>
            {restaurantBranches.length > 0 ? (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {restaurantBranches.slice(0, 10).map((branch) => (
                  <button
                    key={branch.id}
                    className="filter-tag"
                    onClick={() => {
                      setCenterLatitude(branch.latitude.toFixed(6));
                      setCenterLongitude(branch.longitude.toFixed(6));
                    }}
                    title={`${branch.name} (${branch.restaurantName}) - ${branch.phone}`}
                  >
                    📍 {branch.name} ({branch.restaurantName})
                  </button>
                ))}
              </div>
            ) : (
              <div style={{ color: '#666', fontSize: '0.9rem', fontStyle: 'italic' }}>
                {t('searchDriversPage.updateLocationsHint')}
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message" style={{ marginTop: '1rem' }}>
            {error}
          </div>
        )}

        {/* Results */}
        {hasSearched && !loading && (
          <div className="detail-card full-width" style={{ marginTop: '1.5rem' }}>
            <h3>{t('searchDriversPage.searchResults')}</h3>
            <div style={{ marginBottom: '1rem', color: '#666' }} dangerouslySetInnerHTML={{ __html: t('searchDriversPage.foundDrivers', { count: drivers.length, radius: parseFloat(radius).toLocaleString() }) }} />
            <SearchDriversMap
              centerLatitude={parseFloat(centerLatitude)}
              centerLongitude={parseFloat(centerLongitude)}
              radius={parseInt(radius)}
              drivers={drivers}
            />
          </div>
        )}

        {/* Initial State */}
        {!hasSearched && !loading && (
          <div className="detail-card full-width" style={{ marginTop: '1.5rem', textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🗺️</div>
            <h3 style={{ color: '#666' }}>{t('searchDriversPage.enterParameters')}</h3>
            <p style={{ color: '#999' }}>{t('searchDriversPage.enterParametersHint')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchDrivers;